import React, { useState } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    guestCount: '',
    budget: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const form = e.target;
    const data = new FormData(form);

    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data).toString()
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const eventTypes = [
    'Baby Shower',
    'Birthday Party',
    'Bachelorette Party',
    'Bridal Shower',
    'Dinner Party',
    'Brunch',
    'Wine Night',
    'Other'
  ];

  const budgetRanges = [
    'Under $100 (DIY Box)',
    '$100-$200 (Standard Box)',
    '$200-$300 (Luxe Box)',
    '$300-$500 (Service)',
    '$500-$1,000 (Service)',
    '$1,000-$2,500 (Service)',
    '$2,500+ (Full Service)'
  ];

  if (submitted) {
    return (
      <div className="contact-page">
        <section className="contact-success">
          <div className="container">
            <div className="success-content">
              <span className="success-icon">✨</span>
              <h1>Message Received!</h1>
              <p>
                Thank you for reaching out! We're thrilled you're considering Midnight Aurelia Events 
                for your celebration. We'll review your inquiry and get back to you within 
                24-48 hours.
              </p>
              <p className="success-note">
                In the meantime, feel free to browse our party boxes and services for inspiration!
              </p>
              <div className="success-buttons">
                <a href="/party-boxes" className="btn btn-primary">Browse Party Boxes</a>
                <a href="/" className="btn btn-secondary">Back to Home</a>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <span className="section-tag">Get in Touch</span>
          <h1>Let's Create Something Golden Together</h1>
          <p className="hero-subtitle">
            Ready to throw a party people will talk about? Tell us about your vision 
            and we'll handle the rest.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="container">
          <div className="contact-grid">
            {/* Form */}
            <div className="form-wrapper">
              <h2>Tell Us About Your Event</h2>
              <p className="form-intro">
                Fill out the form below and we'll get back to you within 24-48 hours 
                with personalized recommendations.
              </p>

              {/* Hidden form for Netlify to detect */}
              <form name="contact" data-netlify="true" netlify-honeypot="bot-field" hidden>
                <input type="text" name="name" />
                <input type="email" name="email" />
                <input type="tel" name="phone" />
                <select name="eventType"></select>
                <input type="date" name="eventDate" />
                <input type="number" name="guestCount" />
                <select name="budget"></select>
                <textarea name="message"></textarea>
              </form>

              <form 
                name="contact" 
                method="POST" 
                data-netlify="true"
                netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="contact-form"
              >
                <input type="hidden" name="form-name" value="contact" />
                <p hidden>
                  <label>Don't fill this out: <input name="bot-field" /></label>
                </p>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Your Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="jane@email.com"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="eventType">Event Type *</label>
                    <select
                      id="eventType"
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select event type</option>
                      {eventTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="eventDate">Event Date</label>
                    <input
                      type="date"
                      id="eventDate"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="guestCount">Estimated Guest Count</label>
                    <input
                      type="number"
                      id="guestCount"
                      name="guestCount"
                      value={formData.guestCount}
                      onChange={handleChange}
                      placeholder="12"
                      min="1"
                    />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="budget">Budget Range</label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                  >
                    <option value="">Select your budget</option>
                    {budgetRanges.map(range => (
                      <option key={range} value={range}>{range}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="message">Tell Us About Your Vision *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="Share your party dreams! What's the vibe? Any specific colors, themes, or must-haves? The more details, the better we can help..."
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary btn-submit"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div className="contact-info">
              <div className="info-card">
                <h3>Quick Questions?</h3>
                <p>
                  For simple inquiries, feel free to reach out directly. 
                  We typically respond within 24 hours.
                </p>
                <div className="info-item">
                  <span className="info-icon">📧</span>
                  <div>
                    <strong>Email</strong>
                    <a href="mailto:Contact@midnightaureliaevents.com">Contact@midnightaureliaevents.com</a>
                  </div>
                </div>
                <div className="info-item">
                  <span className="info-icon">📍</span>
                  <div>
                    <strong>Service Area</strong>
                    <span>Ships Nationwide • Local Setup in Select Areas</span>
                  </div>
                </div>
              </div>

              <div className="info-card">
                <h3>Not Sure Where to Start?</h3>
                <p>
                  Take our quiz to find your perfect party box, or browse our 
                  services to see which package fits your needs.
                </p>
                <div className="info-links">
                  <a href="/party-boxes" className="info-link">
                    <span>🎁</span> Browse Party Boxes
                  </a>
                  <a href="/services" className="info-link">
                    <span>✨</span> View Services
                  </a>
                </div>
              </div>

              <div className="info-card highlight">
                <h3>Response Time</h3>
                <div className="response-info">
                  <div className="response-item">
                    <span className="time">24-48 hrs</span>
                    <span className="label">General Inquiries</span>
                  </div>
                  <div className="response-item">
                    <span className="time">7-10 days</span>
                    <span className="label">Box Shipping</span>
                  </div>
                  <div className="response-item">
                    <span className="time">2+ weeks</span>
                    <span className="label">Book for Services</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="contact-faq">
        <div className="container">
          <h2>Common Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How far in advance should I book?</h3>
              <p>
                For party boxes, we recommend ordering 2-3 weeks before your event. 
                For styling services, please book at least 3-4 weeks in advance 
                (longer for peak seasons).
              </p>
            </div>
            <div className="faq-item">
              <h3>Do you ship nationwide?</h3>
              <p>
                Yes! Our party boxes ship throughout the US. Flat rate shipping 
                is available, with free shipping on orders over $150.
              </p>
            </div>
            <div className="faq-item">
              <h3>Can I customize a party box?</h3>
              <p>
                Absolutely! Use our contact form to describe your vision, 
                or select add-ons when ordering. We love creating custom experiences.
              </p>
            </div>
            <div className="faq-item">
              <h3>What's your refund policy?</h3>
              <p>
                We require a non-refundable deposit of 25-50% for services. 
                Party boxes can be refunded if returned unused within 7 days.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
